import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  useToast,
  Link,
} from '@chakra-ui/react';
import { pauseSubscription } from '../api';
import { useQueryClient } from 'react-query';

import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import { useGlobalContext } from '../state/GlobalContext';

const PausePromptModal: React.FC = () => {
  const { t } = useTranslation();

  const {
    loadPreviousFlowStep,
    loadNextFlowStep,
    onClose,
  } = useModalManagerContext();
  const { customerId, subscriptionId, authToken } = useGlobalContext();
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();

  const handlePauseButtonClick = async () => {
    toast.closeAll();
    setIsLoading(true);
    try {
      const newSubscription = await pauseSubscription(
        customerId,
        subscriptionId,
        authToken,
      );
      setIsLoading(false);
      toast({
        isClosable: true,
        position: 'top',
        status: 'success',
        title: t('cancelModal.pause.success'),
        variant: 'success',
      });
      onClose();
      const subscription = queryClient.getQueryData<Subscription>([
        'subscription',
        customerId,
        subscriptionId,
      ]);
      queryClient.setQueryData(['subscription', customerId, subscriptionId], {
        ...subscription,
        NextBillingDate: newSubscription.NextBillingDate,
        ItemTypeSubscriptionStatusNextBillingDate:
          newSubscription.ItemTypeSubscriptionStatusNextBillingDate,
        SubscriptionStatus: newSubscription.SubscriptionStatus,
      });
    } catch (err) {
      setIsLoading(false);
      toast({
        isClosable: true,
        position: 'top',
        status: 'error',
        title: (
          <Text color="white">
            {t('cancelModal.error')}{' '}
            <Link onClick={handlePauseButtonClick}>
              {t('cancelModal.tryAgain')}
            </Link>
          </Text>
        ),
        variant: 'error',
      });
      console.log(err);
    }
  };

  return (
    <>
      <ModalHeader>{t('cancelModal.pause.header')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Text variant="strong">{t('cancelModal.pause.title')}</Text>
        <Text>{t('cancelModal.pause.description')}</Text>
        <Button
          onClick={handlePauseButtonClick}
          disabled={isLoading}
          variant="outlinedBig"
          isLoading={isLoading}
        >
          {t('cancelModal.pause.cta')}
        </Button>
      </ModalBody>
      <ModalFooter>
        <Button
          mr="4"
          onClick={loadPreviousFlowStep}
          disabled={isLoading}
          variant="outlinedBig"
        >
          {t('cancelModal.back')}
        </Button>
        <Button
          onClick={loadNextFlowStep}
          disabled={isLoading}
          variant="blackBig"
        >
          {t('cancelModal.continueWithCancellation')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default PausePromptModal;
